import {
  formatCompactNumber,
  type FormatNumberOptions,
} from "@/shared/lib/format-number";
import { cn } from "@/shared/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type CompactNumberProps = {
  value: number | null | undefined;
  locale?: string;
  prefix?: string;
  suffix?: string;
  className?: string;
  formatOptions?: Omit<FormatNumberOptions, "locale">;
  fallback?: React.ReactNode;
  /** Show dotted underline when compact (default: true) */
  indicateCompact?: boolean;
};

export function CompactNumber({
  value,
  locale = "lo-LA",
  prefix = "",
  suffix = "",
  className,
  formatOptions,
  fallback = "–",
  indicateCompact = true,
}: CompactNumberProps) {
  if (value == null || Number.isNaN(value)) {
    return <span className={className}>{fallback}</span>;
  }

  const { display, full, isCompact } = formatCompactNumber(value, {
    locale,
    ...formatOptions,
  });
  const text = `${prefix}${display}${suffix}`;
  const fullText = `${prefix}${full}${suffix}`;

  if (!isCompact) {
    return <span className={cn("tabular-nums", className)}>{text}</span>;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              "tabular-nums",
              indicateCompact &&
                "cursor-default underline decoration-dotted decoration-muted-foreground/50 underline-offset-2",
              className,
            )}
          >
            {text}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p className="tabular-nums">{fullText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
